if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this.offsetGap = 12;
      this.bottomOffsetFrame = null;
      this.boundScheduleBottomOffsetUpdate = this.scheduleBottomOffsetUpdate.bind(this);
      this.widgetObserver = null;

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      window.removeEventListener('resize', this.boundScheduleBottomOffsetUpdate);
      window.removeEventListener('orientationchange', this.boundScheduleBottomOffsetUpdate);
      window.removeEventListener('load', this.boundScheduleBottomOffsetUpdate);

      if (this.widgetObserver) this.widgetObserver.disconnect();
      if (this.bottomOffsetFrame) cancelAnimationFrame(this.bottomOffsetFrame);

      this.setBottomOffset(0);
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
      this.bindBottomOffsetEvents();
    }

    bindBottomOffsetEvents() {
      this.scheduleBottomOffsetUpdate();

      window.addEventListener('resize', this.boundScheduleBottomOffsetUpdate, { passive: true });
      window.addEventListener('orientationchange', this.boundScheduleBottomOffsetUpdate);
      window.addEventListener('load', this.boundScheduleBottomOffsetUpdate);

      this.widgetObserver = new MutationObserver(this.boundScheduleBottomOffsetUpdate);
      this.widgetObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open']
      });
    }

    scheduleBottomOffsetUpdate() {
      if (this.bottomOffsetFrame) return;

      this.bottomOffsetFrame = requestAnimationFrame(() => {
        this.bottomOffsetFrame = null;
        this.updateBottomOffset();
      });
    }

    updateBottomOffset() {
      if (theme.mediaMatches.md || typeof document.elementsFromPoint !== 'function') {
        this.setBottomOffset(0);
        return;
      }

      const style = window.getComputedStyle(this);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const marginLeft = parseFloat(style.marginLeft) || 0;
      const marginRight = parseFloat(style.marginRight) || 0;
      const marginBottom = parseFloat(style.marginBottom) || 0;
      const panelLeft = marginLeft;
      const panelRight = viewportWidth - marginRight;
      const horizontalPoints = [
        Math.max(panelLeft + 24, 24),
        Math.max(panelLeft + 88, 88),
        Math.min(panelRight - 88, viewportWidth - 88),
        Math.min(panelRight - 24, viewportWidth - 24)
      ];
      const verticalPoints = [16, 56, 104, 152, 200]
        .map((offset) => viewportHeight - offset)
        .filter((point) => point > 0);
      const samplePoints = [];

      horizontalPoints.forEach((x) => {
        verticalPoints.forEach((y) => {
          samplePoints.push([Math.min(Math.max(x, 1), viewportWidth - 1), y]);
        });
      });

      const candidates = this.getFixedWidgetCandidates(samplePoints);
      let requiredOffset = 0;

      candidates.forEach((candidate) => {
        const candidateStyle = window.getComputedStyle(candidate);
        const rect = candidate.getBoundingClientRect();

        if (candidateStyle.pointerEvents === 'none') return;
        if (rect.width < 20 || rect.height < 20) return;
        if (rect.right <= panelLeft || rect.left >= panelRight) return;
        if (rect.bottom <= 0 || rect.top >= viewportHeight) return;
        if (rect.width > viewportWidth * 0.85 || rect.height > viewportHeight * 0.5) return;

        requiredOffset = Math.max(
          requiredOffset,
          viewportHeight - rect.top + this.offsetGap - marginBottom
        );
      });

      this.setBottomOffset(Math.ceil(Math.max(requiredOffset, 0)));
    }

    getFixedWidgetCandidates(samplePoints) {
      const seen = new Set();

      return samplePoints.reduce((candidates, [x, y]) => {
        document.elementsFromPoint(x, y).forEach((element) => {
          const candidate = this.getFixedCandidate(element);

          if (!candidate || seen.has(candidate)) return;

          seen.add(candidate);
          candidates.push(candidate);
        });

        return candidates;
      }, []);
    }

    getFixedCandidate(element) {
      let current = element;

      while (current && current !== document.body) {
        if (current === this || this.contains(current)) return null;

        const style = window.getComputedStyle(current);

        if (style.display === 'none' || style.visibility === 'hidden' || Number.parseFloat(style.opacity) === 0) {
          return null;
        }

        if (style.position === 'fixed') return current;

        current = current.parentElement;
      }

      return null;
    }

    setBottomOffset(offset) {
      document.documentElement.style.setProperty('--mobile-fixed-bottom-offset', `${Math.max(offset, 0)}px`);
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
